import { useEffect, useState, useRef } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { eventToAvi, getPubSubClient } from '@confluence/pubsub-client';
import { useUnreadCommentsIsEnabled, useUnreadInlineComments } from '@confluence/unread-comments';

import {
	INACTIVE_TAB_WAIT_INTERVAL,
	PageMode,
	PubSubFlagType,
	PubSubState,
} from './pubSubConstants';
import type {
	OnCreatedCommentEvent,
	OnInlineCommentActionEvent,
	OnInlineCommentActionParams,
	OnPageCommentActionParams,
	OnPublishEvent,
	PubSubData,
	PubSubSubscriptionProps,
	PubSubSubscriptionResultsProps,
} from './pubSubDataTypes';
import { getUserInfo, onInlineCommentAction, onPageCommentAction } from './pubSubUtils';
import type { UserInfo } from './pubSubUtils';

let subscriptionShutdownTimer;

export const usePubsubSubscriptions = ({
	contentId,
	contentType,
	documentVisible,
	pageMode,
	currentAccountId,
	documentNeedsUpdate,
	isFabricPage,
}: PubSubSubscriptionProps): PubSubSubscriptionResultsProps => {
	const [pubSubData, setPubSubData] = useState<PubSubData | undefined>(undefined);
	const [pubSubState, setPubSubState] = useState<PubSubState>(PubSubState.PUBSUB_ONLY);
	const documentNeedsUpdateRef = useRef(documentNeedsUpdate);
	useEffect(() => {
		documentNeedsUpdateRef.current = documentNeedsUpdate;
	}, [documentNeedsUpdate]);

	const [errors, setErrors] = useState<boolean>(false);
	// Persistent storage to use for tracking whether content IDs match for sub/unsub
	const subscribedContentIdRef = useRef<string | null>(null);
	const subscribedEventsRef = useRef<Subscriber[]>([]);

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const pubSubResourceType = contentType || 'page';

	const [
		{ readCommentsListState, markerRefToCommentIdMap },
		{ updateUnreadCommentsListState, updateMarkerRefToCommentIdMap },
	] = useUnreadInlineComments();

	const isUnreadCommentsEnabled = useUnreadCommentsIsEnabled();

	const inlineCommentActionParams: OnInlineCommentActionParams = {
		currentAccountId,
		isUnreadCommentsEnabled,
		readCommentsListState,
		updateUnreadCommentsListState,
		pageMode,
		contentId,
		setPubSubData,
		markerRefToCommentIdMap,
		updateMarkerRefToCommentIdMap,
		documentNeedsUpdate: documentNeedsUpdateRef,
		isFabricPage,
	};

	const pageCommentActionParams: OnPageCommentActionParams = {
		setPubSubData,
	};

	const subscribeToPubSubs = async () => {
		try {
			const client = await getPubSubClient();
			await client.joinChannel(pubSubResourceType, contentId);

			checkPubsubContentIdMismatch(contentId);

			const subscribers = getSubscribersByPageMode();

			for (const subscriber of subscribers) {
				void client
					.subscribe(subscriber.eventToAvi, subscriber.callback)
					.catch(getPubSubErrorHandler);
			}

			subscribedEventsRef.current = subscribers;
		} catch (e) {
			getPubSubErrorHandler(e);
		}
	};

	const unsubscribeFromAllPubSubs = async () => {
		const client = await getPubSubClient();
		void client.leaveChannel(pubSubResourceType, contentId);
		subscribedContentIdRef.current = null;

		// Iterate over the subscribed events and unsubscribe
		for (const subscriber of subscribedEventsRef.current) {
			void client
				.unsubscribe(subscriber.eventToAvi, subscriber.callback)
				.catch(getPubSubErrorHandler);
		}

		// Clear the subscribed events
		subscribedEventsRef.current = [];
	};

	const getPubSubErrorHandler = (_error) => {
		setPubSubState(PubSubState.POLLING_ONLY);
		setErrors(true);
	};

	const getSubscribersByPageMode = (): Subscriber[] => {
		// Common subscribers across all page modes
		const commonSubscribers: Subscriber[] = [
			resolvedInlineCommentPubSubParams,
			unresolvedInlineCommentPubSubParams,
			deletedInlineCommentPubSubParams,
		];

		if (pageMode === PageMode.EDITOR) {
			return [
				...commonSubscribers,
				// the backend will send these events for new comments and replies created in Editor
				createRendererInlineCommentPubSubParams,
				createEditorInlineCommentPubSubParams,
			];
		}

		if (pageMode === PageMode.LIVE) {
			return [
				...commonSubscribers,
				createInlineCommentPubSubParams,
				reattachInlineCommentPubSubParams,
			];
		}

		if (pageMode === PageMode.RENDERER) {
			return [
				...commonSubscribers,
				createInlineCommentPubSubParams,
				reattachInlineCommentPubSubParams,
				publishPagePubSubParams,
				publishBlogPubSubParams,
				createEditorInlineCommentPubSubParams, // we need to listen for replies created in Editor
				createCommentPubSubParams,
			];
		}

		return commonSubscribers;
	};

	/**
	 * Checks if the contentId that the user is attempting to subscribe to
	 * is different from the contentId that they are currently subscribed to. Fires an analytics event if there is a mismatch.
	 *
	 * Note: This function was added to detect and handle cases where attempts are made to subscribe to a different
	 * contentId without unsubscribing from the previous contentId, which could lead to unexpected behavior.   *
	 * @param {string} attemptedToSubscribeContentId - The contentId that the user is attempting to subscribe to
	 */
	const checkPubsubContentIdMismatch = (attemptedToSubscribeContentId: string) => {
		if (subscribedContentIdRef.current !== null) {
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					action: 'mismatch',
					actionSubject: 'pubsub',
					source: 'usePubSubs',
					attributes: {
						attemptedToSubscribeContentId,
						existingSubscribedContentId: subscribedContentIdRef.current,
					},
				},
			}).fire();
		}
	};

	const onPublish = (_eventName, eventData: OnPublishEvent) => {
		void getUserInfo(eventData.accountId).then((userInfo: UserInfo | undefined) => {
			const { pageUpdateRequestBody, accountId } = eventData;
			setPubSubData({
				displayName: userInfo?.displayName,
				profilePicturePath: userInfo?.profilePicturePath,
				accountId,
				flagType: PubSubFlagType.EDIT,
				...pageUpdateRequestBody,
			});
		});
	};

	const onInlineCommentActionFn = (_eventName: string, eventData: OnInlineCommentActionEvent) =>
		onInlineCommentAction(_eventName, eventData, inlineCommentActionParams);

	const onPageCommentActionFn = (_eventName: string, eventData: OnCreatedCommentEvent) =>
		onPageCommentAction(_eventName, eventData, pageCommentActionParams);

	type Subscriber = {
		eventToAvi: string;
		callback: (_eventName: any, eventData: any) => void | Promise<void>;
	};

	const createInlineCommentPubSubParams: Subscriber = {
		eventToAvi: eventToAvi('created:inline_comment'),
		callback: onInlineCommentActionFn,
	};

	const createRendererInlineCommentPubSubParams: Subscriber = {
		eventToAvi: eventToAvi('created:renderer_inline_comment'),
		callback: onInlineCommentActionFn,
	};

	const createEditorInlineCommentPubSubParams: Subscriber = {
		eventToAvi: eventToAvi('created:editor_inline_comment'),
		callback: onInlineCommentActionFn,
	};

	const resolvedInlineCommentPubSubParams: Subscriber = {
		eventToAvi: eventToAvi('resolved:inline_comment'),
		callback: onInlineCommentActionFn,
	};

	const unresolvedInlineCommentPubSubParams: Subscriber = {
		eventToAvi: eventToAvi('unresolved:inline_comment'),
		callback: onInlineCommentActionFn,
	};

	const reattachInlineCommentPubSubParams: Subscriber = {
		eventToAvi: eventToAvi('reattach:inline_comment'),
		callback: onInlineCommentActionFn,
	};

	const deletedInlineCommentPubSubParams: Subscriber = {
		eventToAvi: eventToAvi('deleted:inline_comment'),
		callback: onInlineCommentActionFn,
	};

	const createCommentPubSubParams: Subscriber = {
		eventToAvi: eventToAvi('created:comment'),
		callback: onPageCommentActionFn,
	};
	const publishPagePubSubParams: Subscriber = {
		eventToAvi: eventToAvi('publish:page'),
		callback: onPublish,
	};
	const publishBlogPubSubParams: Subscriber = {
		eventToAvi: eventToAvi('publish:blogpost'),
		callback: onPublish,
	};

	useEffect(() => {
		void subscribeToPubSubs();
		const handleBeforeUnload = () => {
			void unsubscribeFromAllPubSubs();
		};

		window.addEventListener('beforeunload', handleBeforeUnload);
		return () => {
			window.removeEventListener('beforeunload', handleBeforeUnload);
			void unsubscribeFromAllPubSubs();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [contentId, contentType]);

	//  Uses a timer in the form of a setTimeout to unsubscribe from pubsubevents if a user has left a tab for more than 2 seconds
	useEffect(() => {
		if (documentVisible && subscriptionShutdownTimer) {
			clearTimeout(subscriptionShutdownTimer);
			subscriptionShutdownTimer = null;
		}
		if (!documentVisible && !subscriptionShutdownTimer) {
			subscriptionShutdownTimer = setTimeout(() => {
				void unsubscribeFromAllPubSubs();
				setPubSubState(PubSubState.TERMINATED);
			}, INACTIVE_TAB_WAIT_INTERVAL);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [documentVisible]);

	useEffect(() => {
		setPubSubState((prevState) => {
			if (documentVisible && prevState === PubSubState.TERMINATED) {
				void subscribeToPubSubs();
				return PubSubState.ACTIVE;
			}
			return prevState;
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [documentVisible]);

	return {
		pubSubData,
		pubSubState,
		errors,
		canUsePubSubData: pubSubState !== PubSubState.POLLING_ONLY,
	};
};
